import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { BaseIssue } from 'valibot';

import { TextField } from '@mui/material';

import InputMask from '@mona-health/react-input-mask';
import { useFieldContext } from '#form';

import { LabelInput } from '@repo/ui/LabelInput';
import { SlideToggle } from '@repo/ui/SlideToggle';
import { fixTime } from '@repo/ui/utils/dateTime';

import { convertTime } from './helpers/time';
import { timeInputKeyHandlers } from './helpers/timeInputHandlers';

interface InputProps {
	label: ReactNode;
	defaultMeridian?: 'AM' | 'PM';
	disabled?: boolean;
	use24Hour?: boolean;
}
export function TimeInput({
	label,
	defaultMeridian = 'AM',
	disabled,
	use24Hour = false,
}: InputProps) {
	const field = useFieldContext<string | null | undefined>();

	const formValue = field.state.value ?? '';

	const error = (
		field.state.meta as {
			errors?: Array<string | BaseIssue<unknown>>;
		}
	).errors?.[0];

	const nudgeTime = (direction: 'up' | 'down', isHours: boolean) => {
		const fixedTime = fixTime(formValue);
		if (!fixedTime) return;
		const [hours, minutes] = fixedTime.split(':');
		let newHours = parseInt(hours ?? '0', 10);
		let newMinutes = parseInt(minutes ?? '0', 10);

		const mod = direction === 'up' ? 1 : -1;

		if (isHours) {
			newHours = (newHours + mod + 24) % 24;
		} else {
			newMinutes = (newMinutes + mod + 60) % 60;
		}

		field.handleChange(
			`${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`,
		);
		field.handleBlur();
	};

	const setMeridian = (value: 'AM' | 'PM') => {
		if (use24Hour) return;
		let fixedTime = fixTime(field.state.value);

		if (!fixedTime) return;

		const [hours, minutes] = fixedTime.split(':');
		const hoursNum = parseInt(hours ?? '0', 10);

		if (value === 'PM') {
			// we've toggled to PM, so add 12 hours to the time
			if (hours && hoursNum >= 0 && hoursNum < 12) {
				fixedTime = `${hoursNum + 12}:${minutes}`;
			}
		} else {
			// we've toggled to AM, so subtract 12 hours from the time
			if (hours && hoursNum >= 12 && hoursNum < 24) {
				fixedTime = `${String(hoursNum - 12).padStart(2, '0')}:${minutes}`;
			}
		}

		if (fixedTime && field.state.value !== fixedTime) {
			field.handleChange(fixedTime);
		}
		field.handleBlur();
	};

	// convert the 24 hour clock time to a 12 hour clock time
	// and extract the AM/PM meridian. When `use24Hour` is true we
	// display the stored 24-hour value directly and hide the meridian.
	const [time, setTime] = useState(() =>
		use24Hour ?
			{ time: formValue, meridian: defaultMeridian }
		:	(convertTime(formValue) ?? { time: formValue, meridian: defaultMeridian }),
	);

	useEffect(() => {
		// eslint-disable-next-line react-x/set-state-in-effect
		setTime(
			use24Hour ?
				{ time: formValue, meridian: defaultMeridian }
			:	(convertTime(formValue) ?? {
					time: formValue,
					meridian: defaultMeridian,
				}),
		);
	}, [defaultMeridian, formValue, use24Hour]);

	return (
		<LabelInput label={label} fieldName={field.name}>
			<InputMask
				mask="99:99"
				maskPlaceholder={null}
				disabled={disabled}
				value={disabled ? '' : time.time}
				onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
					let fixedTime = fixTime(e.target.value);
					if (!use24Hour && fixedTime) {
						const [hours, minutes] = fixedTime.split(':');
						const hoursNum = parseInt(hours ?? '0', 10);
						if (time.meridian === 'PM') {
							// if the meridian is PM, we need to add 12 hours to the time
							if (hoursNum >= 0 && hoursNum < 12) {
								fixedTime = `${hoursNum + 12}:${minutes}`;
							}
						} else if (hoursNum === 12) {
							fixedTime = `00:${minutes}`;
						}
					}
					field.handleChange(fixedTime);
					field.handleBlur();
				}}
				onChange={(e: ChangeEvent<HTMLInputElement>) => {
					const value = e.target.value;
					setTime((prev) => ({
						...prev,
						time: value,
					}));
				}}
				{...timeInputKeyHandlers(nudgeTime)}
				placeholder="hh:mm"
				beforeMaskedStateChange={({
					currentState,
					nextState,
				}: {
					currentState?: {
						value: string;
					};
					nextState: {
						value: string;
						enteredString: string;
						selection: { start: number; end: number };
					};
				}) => {
					const minutes = currentState?.value.split(':').at(-1) ?? '';

					if (nextState.selection.start === 1) {
						// If the user is entering the first digit of the hour
						if (nextState.enteredString === ':') {
							// If the user has entered a colon, we need to insert a leading zero
							// to the existing hour digit and move the cursor to the minutes
							return {
								value: `0${currentState?.value.split(':')[0]}:${minutes}`,
								selection: { start: 3, end: 3 },
							};
						}

						// For 24-hour mode, allow 0-2 as first digit (for 00-23)
						// For 12-hour mode, allow 0-1 as first digit (for 01-12)
						const maxFirstDigit = use24Hour ? 2 : 1;

						if (+nextState.enteredString > maxFirstDigit) {
							// If the user has entered a number greater than the max first digit
							// insert a leading zero and move the cursor to the minutes
							return {
								value: `0${nextState.enteredString}:${minutes}`,
								selection: { start: 3, end: 3 },
							};
						}
						if (
							+nextState.enteredString <= maxFirstDigit &&
							nextState.value.length > 2
						) {
							// If the user has entered a valid first digit into an already populated time
							// append a zero to be over-typed by the next digit, keeping the minutes in place
							return {
								value: `${nextState.enteredString}0:${minutes}`,
								selection: { start: 1, end: 1 },
							};
						}
					}

					return {
						...nextState,
					};
				}}
			>
				<TextField
					id={field.name}
					name={field.name}
					sx={{
						'& .MuiInputBase-adornedEnd': {
							paddingRight: 0.5,
						},
						'& .MuiInputBase-root.Mui-disabled': {
							backgroundColor: 'grey.100',
						},
						'& input': { fontVariantNumeric: 'tabular-nums' },
					}}
					error={error != null}
					helperText={
						error ?
							typeof error === 'string' ?
								error
							:	error.message
						:	''
					}
					slotProps={{
						input: {
							endAdornment:
								!use24Hour ?
									<SlideToggle
										value={time.meridian}
										options={['AM', 'PM']}
										setValue={setMeridian}
										disabled={disabled}
									/>
								:	undefined,
						},
					}}
				/>
			</InputMask>
		</LabelInput>
	);
}
